import { APP_BASE_HREF } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SessionStorageService } from 'ngx-webstorage';
import { SwUpdate } from '@angular/service-worker';
import { catchError, from, map, of, switchMap, timer } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService
{
  // URL to check (the root index.html file)
  private checkURL = 'index.html';

  // Check every 5 minutes
  private checkInterval = 1000 * 60 * 5;

  // Store a reference to the initially loaded index.html content (now using the Meta Build Version)
  private initialContent: string | null = null;

  // Prevent overlapping prompts while keeping the timer running
  private promptOpen = false;

  // Single short re-check after the first baseline capture to avoid waiting full interval
  private quickRecheckDelay = 2000; // 2 seconds
  private quickRecheckScheduled = false;

  constructor(
    private sessionStorageService: SessionStorageService,
    private translateService: TranslateService,
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Optional() private swUpdate?: SwUpdate) 
  {
    // Capture the version immediately from the DOM as the baseline upon service instantiation
    const metaVersion = document.querySelector('meta[name="build-version"]')?.getAttribute('content');
    this.initialContent = metaVersion || 'unknown';
  }

  public initVersionCheck(): void
  {
    this.initServiceWorkerUpdateHandling();

    // Start the periodic timer check
    timer(0, this.checkInterval).pipe(
      switchMap(() => this.checkVersion())
    ).subscribe({
      next: (needsUpdate) =>
      {
        this.handleUpdate(needsUpdate);

        // Schedule one quick follow-up check soon after the baseline is captured
        // Note: initialContent is now set in constructor, so this handles immediate post-load checks
        if (this.initialContent !== null && !this.quickRecheckScheduled)
        {
          this.quickRecheckScheduled = true;
          setTimeout(() => this.checkVersion().subscribe(v => this.handleUpdate(v)), this.quickRecheckDelay);
        }
      }
    });
  }

  private initServiceWorkerUpdateHandling(): void
  {
    // If the Angular service worker is available, let it signal when a new version is ready
    if (!this.swUpdate || !this.swUpdate.isEnabled)
    {
      return;
    }

    this.swUpdate.versionUpdates.subscribe((event) =>
    {
      if (event.type === 'VERSION_READY')
      {
        // Activate the new version and reload to ensure fresh assets are served
        this.swUpdate.activateUpdate().then(() =>
        {
          window.location.href = this.buildReloadUrl();
        });
      }
    });

    // Ask the service worker to check immediately on startup instead of waiting for its own cadence
    this.swUpdate.checkForUpdate().catch(() => { });
  }

  private checkVersion()
  {
    // Append a unique parameter to the URL to bypass any client-side (Angular)
    const normalizedBase = this.baseHref.endsWith('/') ? this.baseHref : `${this.baseHref}/`;
    const url = new URL(this.checkURL, `${window.location.origin}${normalizedBase}`).toString();
    const params = new HttpParams()
      .set('t', new Date().getTime().toString())
      .set('ngsw-bypass', 'true'); // ask Angular service worker to skip cache

    // Use fetch with cache: 'reload' to force the network and avoid any persisted caches
    const fetchUrl = `${url}?${params.toString()}`;

    // Added ngsw-bypass header to match sw.js bypass logic
    return from(fetch(fetchUrl, {
      cache: 'reload',
      headers: { 'ngsw-bypass': 'true' },
      credentials: 'same-origin'
    })).pipe(
      switchMap((response) =>
      {
        if (!response.ok)
        {
          const raw = this.translateService.instant('ERROR-VERSION-CHECK-FAILED', { status: response.status });
          // Ensure {{status}} gets replaced even if interpolation is not configured
          const message = typeof raw === 'string'
            ? raw.replace('{{status}}', response.status.toString())
            : raw;
          throw new Error(message || `Version check failed with status ${response.status}`);
        }
        return response.text();
      }),
      map((newHtmlContent: string) =>
      {
        // Parse the string into a temporary DOM object to extract the injected Jenkins version
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(newHtmlContent, 'text/html');
        const newVersion = newDoc.querySelector('meta[name="build-version"]')?.getAttribute('content');

        // Return the boolean indicating if an update is needed by comparing the fetched meta tag to the initial baseline
        return (newVersion !== this.initialContent && newVersion !== null) || this.sessionStorageService.retrieve('reload');
      }),
      // Keep the polling alive even if a request fails (e.g., offline/404)
      catchError(() => of(false))
    );
  }

  private handleUpdate = (needsUpdate: boolean): void =>
  {
    if (needsUpdate && !this.promptOpen)
    {
      this.promptOpen = true;
      this.promptUserForReload();
    }
  };

  private promptUserForReload(): void
  {
    this.translateService.get(['NEW-VERSION-AVAILABLE']).subscribe({
      next: (res) =>
      {
        if (confirm(res['NEW-VERSION-AVAILABLE']))
        {
          window.location.href = this.buildReloadUrl();
          this.sessionStorageService.clear('reload')
        }
        this.promptOpen = false;
      },
      error: () =>
      {
        this.promptOpen = false;
      }
    });
  }

  /**
   * Build a reload URL that bypasses Angular service worker caches to avoid
   * reloading an outdated bundle, while preserving the current route and hash.
   */
  private buildReloadUrl(): string
  {
    const url = new URL(window.location.href);
    url.searchParams.set('ngsw-bypass', 'true');
    url.searchParams.set('t', Date.now().toString());
    return url.toString();
  }
}